import { FilloutStandardEmbed } from "@fillout/react";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  Box,
  Button,
  Container,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import { PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import {
  addFilledOut,
  ProgrammeActions,
  selectAllProgrammeActions,
  setQuickAssessmentFinished,
  setQuickAssessmentStarted,
  useAppDispatch,
  useAppSelector,
} from "model";
import {
  fetchAllQuickAssessmentResults,
  NO_PROGRAMM_ID,
  selectQuickAssessmentResultsById,
} from "model/quickAssessmentSlice";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import CenteredTextBox from "./CenteredTextBox";
import StepCard from "./FirstStepCard";
import NavigationLock from "./NavigationLock";
import ProgrammeBanner from "./ProgrammeBanner";

const REFRESH_INTERVAL = 1 * 1000;

export default function ProgrammesList() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user.details);
  const { programmeId } = useParams<{ programmeId: string }>();

  const programmesActions = useAppSelector((state) =>
    selectAllProgrammeActions(state)
  );

  const sortedProgrammesActions = programmesActions.sort(
    (a: ProgrammeActions, b: ProgrammeActions) => {
      return a.programmeName > b.programmeName ? 1 : -1;
    }
  );

  const loading = useAppSelector(
    (state) => state.programmeActions.loading === "pending"
  );

  const onSubmit = () => {
    dispatch(addFilledOut({ filledOutProgrammeId: NO_PROGRAMM_ID }));
    dispatch(setQuickAssessmentFinished(true));
  };

  const quickAssessmentStarted = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).quickAssessmentStarted
  );

  const quickAssessmentResults = useAppSelector((state) =>
    selectQuickAssessmentResultsById(state, NO_PROGRAMM_ID)
  );

  const hasFilledOutQuickAssessment =
    quickAssessmentResults?.submitted === true;

  const programmeActionsQuestionnaireFilledOutIds = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).programmeActionsQuestionnaireFilledOutIds
  );

  const filledOutButResultNotAvailableYet =
    programmeActionsQuestionnaireFilledOutIds.programmeActionsQuestionnaireFilledOutIds.includes(
      NO_PROGRAMM_ID
    ) && !hasFilledOutQuickAssessment;

  const loadingQuickAssessment = useAppSelector(
    (state) => state.quickAssessmentResults.loading === "pending"
  );

  // Handle form submission message from iframe
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleMessages = (content: any) => {
      if (!content) {
        return;
      }
      // Check if submission is completed.
      if (
        typeof content.data === "object" &&
        content.data.action === "submission-completed"
      ) {
        dispatch(addFilledOut({ filledOutProgrammeId: NO_PROGRAMM_ID }));
        dispatch(setQuickAssessmentFinished(false));
        return;
      }
    };

    if (window.addEventListener) {
      window.addEventListener("message", handleMessages, true); // Capture
    }
    return () => {
      if (window.removeEventListener) {
        window.removeEventListener("message", handleMessages, true);
      }
    };
  }, [dispatch, programmeId]);

  // Listen to route changes and cleanup quickAssessmentStarted state when navigating away
  useEffect(() => {
    const unsetQuickAssessmentStarted = history.listen((e) => {
      if (e.pathname !== "/programmes") {
        // User is navigating away from programmes page
        dispatch(setQuickAssessmentFinished(false));
      }
    });

    return function cleanup() {
      unsetQuickAssessmentStarted();
    };
  }, [dispatch, history]);

  // Prevent accidental page close/refresh when questionnaire is in progress
  useEffect(() => {
    const onBeforeUnload = (ev: Event) => {
      if (quickAssessmentStarted.quickAssessmentStarted) {
        ev.preventDefault();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickAssessmentStarted.quickAssessmentStarted]);

  // Poll for quick assessment results after form submission
  useEffect(() => {
    if (filledOutButResultNotAvailableYet) {
      const reloadQuickAssessmentResults = async () => {
        if (!loadingQuickAssessment) {
          await dispatch(fetchAllQuickAssessmentResults());
        }
        return;
      };
      const refreshInterval = setInterval(
        reloadQuickAssessmentResults,
        REFRESH_INTERVAL
      );
      // Clear interval when results are available
      if (hasFilledOutQuickAssessment) {
        clearInterval(refreshInterval);
      }
      return () => clearInterval(refreshInterval);
    }
  }, [
    dispatch,
    filledOutButResultNotAvailableYet,
    loadingQuickAssessment,
    hasFilledOutQuickAssessment,
  ]);

  // Show loading indicator while waiting for results
  if (loading || loadingQuickAssessment || filledOutButResultNotAvailableYet) {
    return <LinearProgress />;
  }

  // Render content section (programmes list or no-programmes message)
  const renderContent = () => {
    // Show "no programmes" content when there are no programmes
    if (sortedProgrammesActions.length === 0) {
      const maxHeight = "auto";

      if (!hasFilledOutQuickAssessment) {
        return (
          <Box
            sx={{
              backgroundColor: "white",
              p: 3,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                maxWidth: "600px",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 5,
              }}
            >
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontSize: "28px",
                  fontWeight: 600,
                  mb: 3,
                  color: "#000",
                  textAlign: "center",
                }}
              >
                Join a programme to get the best out of ruumi!
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#666",
                  lineHeight: 1.5,
                  textAlign: "left",
                  mb: 3,
                }}
              >
                {t("no.programmes.available.body.web")}
              </Typography>

              {!quickAssessmentStarted.quickAssessmentStarted &&
                !hasFilledOutQuickAssessment && (
                  <StepCard
                    stepNumber={1}
                    stepTitle="Do a quick assessment"
                    stepDescription="The first step is to tell us a bit about your farm."
                    buttonText="TELL US ABOUT YOUR FARM"
                    daysLeft={122}
                    status="In progress"
                    onButtonClick={() => {
                      dispatch(setQuickAssessmentStarted(true));
                    }}
                  />
                )}
            </Box>
          </Box>
        );
      } else {
        return (
          <Box
            sx={{
              backgroundColor: "white",
              p: 3,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "calc(100vh - 100px)",
            }}
          >
            <Box sx={{ maxWidth: "600px", width: "100%" }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontSize: "28px",
                  fontWeight: 600,
                  mb: 3,
                  color: "#000",
                  textAlign: "center",
                }}
              >
                Thank you for taking the time to tell us more about your farm!
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#666",
                  lineHeight: 1.5,
                  textAlign: "left",
                }}
              >
                {t("quick.assessment.thank.you.web")}
              </Typography>
            </Box>
          </Box>
        );
      }
    }

    // Show programmes list
    return (
      <Box>
        <PageTitleBar title={t("programmes.toolbar.title")} showDivider />
        <Container
          maxWidth={false}
          disableGutters
          sx={{
            bgcolor: "white",
            padding: 3,
          }}
        >
          {sortedProgrammesActions.map((programme) => {
            return (
              <Box
                key={programme.guid}
                sx={{
                  bgcolor: "white",
                  borderRadius: "8px",
                  border: "1px solid #e4e4e7",
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                  mb: 3,
                  overflow: "hidden",
                }}
              >
                {/* Banner Image */}
                <ProgrammeBanner programme={programme} />

                {/* Card Content */}
                <Box sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="h5"
                      component="h2"
                      sx={{
                        fontSize: "20px",
                        fontWeight: 600,
                        color: "#000",
                        flex: 1,
                      }}
                    >
                      {programme.programmeName}
                    </Typography>

                    <Button
                      variant="contained"
                      onClick={() => {
                        history.push(`/programme-details/${programme.guid}`);
                      }}
                      sx={{
                        bgcolor: "#1C4321",
                        color: "white",
                        fontWeight: 600,
                        textTransform: "uppercase",
                        fontSize: "12px",
                        px: 3,
                        py: 1,
                        "&:hover": {
                          bgcolor: "#2E7831",
                        },
                        ml: 2,
                        flexShrink: 0,
                      }}
                    >
                      {t("programmes.viewProgramme")}
                    </Button>
                  </Box>

                  {/* Welcome Text Preview */}
                  <Box sx={{ mb: 2 }}>
                    <Grid container>
                      <Grid
                        item
                        xs={12}
                        sm={10}
                        md={8}
                        sx={{ display: "flex", justifyContent: "flex-start" }}
                      >
                        <CenteredTextBox
                          text={
                            programme.welcomeText?.replace(
                              "FARMER_NAME",
                              user?.name ?? ""
                            ) ?? ""
                          }
                          useMaxSpace={true}
                          maxHeight="auto"
                          lineHeight={20}
                          variant="body2"
                          style={{ color: "#666", margin: 0 }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Status Indicator */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <AccessTimeIcon sx={{ fontSize: "20px", color: "#666" }} />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontWeight: 500,
                      }}
                    >
                      {t("programmes.inProgress")}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Container>
      </Box>
    );
  };

  // Render iframe section when quick assessment is started
  const renderIframe = () => {
    if (!quickAssessmentStarted.quickAssessmentStarted) {
      return null;
    }

    return (
      <Box
        sx={{
          backgroundColor: "white",
          borderRadius: 0,
          boxShadow: 0,
          padding: 0,
          margin: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "row",
          height: "100vh",
          width: "100%",
          "& iframe": {
            width: "100% !important",
            maxWidth: "100% !important",
          },
          "& > div": {
            width: "100% !important",
            "& iframe": {
              width: "100% !important",
              maxWidth: "100% !important",
            },
          },
        }}
      >
        <FilloutStandardEmbed
          filloutId="f9fYsLghULus"
          onSubmit={onSubmit}
          parameters={{
            userGuid: user?.guid,
            programmeGuid: NO_PROGRAMM_ID,
            email: user?.mail,
          }}
        />
      </Box>
    );
  };

  return (
    <Box>
      <NavigationLock when={quickAssessmentStarted.quickAssessmentStarted} />
      {quickAssessmentStarted.quickAssessmentStarted
        ? renderIframe()
        : renderContent()}
    </Box>
  );
}
